import { type FC } from 'react'

import { type PimProductWithVariantsV1 } from '@redteclab/api/clients/bully'

import { priceGetFormattedPrice } from '../../../price/priceGetFormattedPrice'
import { useProductPrices } from '../../../product-page/components/ProductBuyBox/hooks/useProductPrices'
import { RetailPrice } from '../../../product-page/components/ProductBuyBox/ProductVariantHeader/RetailPrice'
import { productAccessorGetLowestPrice } from '../../../product-page/model/productAccessor/productAccessorGetLowestPrice'
import { EXPERIMENT_VARIATION } from '../../model/EXPERIMENT_VARIATION'

import { ExperimentOwnBrandsOverlayAddToCartGroup } from './ExperimentOwnBrandsOverlayAddToCartGroup'
import { ExperimentOwnBrandsOverlayImage } from './ExperimentOwnBrandsOverlayImage'
import { ExperimentOwnBrandsProductDeliveryInfo } from './ExperimentOwnBrandsProductDeliveryInfo'
import { ExperimentOwnBrandsProductRating } from './ExperimentOwnBrandsProductRating'
import { ExperimentOwnBrandsProductStockStatus } from './ExperimentOwnBrandsProductStockStatus'
import { useExperimentOwnBrandsOverlayProductProperties } from './useExperimentOwnBrandsOverlayProductProperties'

export const ExprimentOwnBrandsOverlayProductV1: FC<{
  ownBrandsProduct: PimProductWithVariantsV1
}> = ({ ownBrandsProduct }) => {
  const {
    currentPrescriptionType,
    href,
    intl,
    manufacturer,
    name,
    offers,
    productAttributes,
    rating,
    upid,
    variants,
  } = useExperimentOwnBrandsOverlayProductProperties(ownBrandsProduct)

  const {
    discount,
    displayPrice,
    formattedUnitPrice,
    isDiscountValid,
    isOfferMirakl,
    isProductNotInCatalog,
    isProductOutOfSale,
    isProductOutOfStock,
    strikeThroughOfferPrice,
  } = useProductPrices({
    currentPrescriptionType,
    offerId: offers?.[0]?.offerId,
    product: ownBrandsProduct,
  })
  const isProductNotInStock =
    isProductNotInCatalog || isProductOutOfSale || isProductOutOfStock

  return (
    <section
      className="relative pt-2 tablet:flex tablet:pt-0"
      data-product-id={upid}
    >
      <ExperimentOwnBrandsOverlayImage
        discount={discount}
        product={ownBrandsProduct}
        variation={EXPERIMENT_VARIATION.V1}
      />
      <div className="flex flex-1 flex-col items-start justify-start px-1 tablet:px-2.5">
        {name}
        {rating && rating.count > 0 ? (
          <ExperimentOwnBrandsProductRating
            href={`${href}#user-reviews-section`}
            rating={rating}
          />
        ) : null}

        <div className="flex">
          <RetailPrice
            className="text-l font-bold text-dark-accent"
            currentPrescriptionType={currentPrescriptionType}
            data-qa-id="product-price"
            isDiscountValid={isDiscountValid}
            isMasterPage={false}
            lowestPrice={productAccessorGetLowestPrice(variants)}
            offerPrice={displayPrice}
          />

          {isDiscountValid && offers ? (
            <div className="pl-1 pt-1.5 text-xs text-dark-primary-strong line-through">
              {priceGetFormattedPrice(
                strikeThroughOfferPrice?.price ?? 0,
                intl,
              )}
            </div>
          ) : null}
        </div>

        <span className="text-xs text-dark-primary-strong">
          {formattedUnitPrice}
        </span>
        <ul className="list-disc pl-5 text-xs text-dark-primary-strong">
          <li>{productAttributes}</li>
          <li>{`PZN: ${upid}`}</li>
          <li>{manufacturer?.name}</li>
        </ul>
        <div className="flex w-full flex-row justify-between pt-2">
          <ExperimentOwnBrandsProductStockStatus product={ownBrandsProduct} />
          <ExperimentOwnBrandsProductDeliveryInfo
            {...{
              currentPrescriptionType,
              isMarketplace: isOfferMirakl,
              offer: offers?.[0],
              offerId: offers ? offers[0]?.offerId : null,
              product: ownBrandsProduct,
            }}
          />
        </div>
        {!isProductNotInStock && (
          <ExperimentOwnBrandsOverlayAddToCartGroup
            product={ownBrandsProduct}
          />
        )}
      </div>
    </section>
  )
}
